import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Save, Eye, Send, ArrowLeft, X, Plus, Loader2, CheckCircle,
  Tag, FileText, Search, Type, AlignLeft, Hash, Globe, EyeOff, Clock, Wifi
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useAutoSaveDraft } from "../../hooks/useArticles";

const SEO_TIPS = [
  "Используйте ключевое слово в заголовке статьи",
  "Разделите текст на 2-3 раздела с подзаголовками (используйте ## в редакторе), включив в них вариации ключевых слов",
  "Первый абзац должен содержать главное ключевое слово",
  "Оптимальная длина статьи: 1500-3000 слов",
  "Добавьте 3-5 тегов, релевантных теме статьи",
  "Meta-описание должно быть 120-160 символов и содержать CTA"
];

const STRUCTURE_TEMPLATE = `## Введение

Опишите проблему или вопрос, который вы будете освещать. Почему это важно для вашей аудитории?

## Основная часть

### Ключевой тезис 1

Раскройте первый важный аспект темы. Приведите примеры из практики.

### Ключевой тезис 2

Продолжите с другим аспектом. Используйте данные или кейсы.

### Ключевой тезис 3

Третий аспект или рекомендация.

## Практические рекомендации

Перечислите конкретные шаги, которые читатель может предпринять:

1. Первый шаг
2. Второй шаг
3. Третий шаг

## Заключение

Подведите итоги и предложите следующий шаг (записаться на консультацию, задать вопрос).`;

const TEMPLATE_LINES = STRUCTURE_TEMPLATE.split("\n");

function generateSlugFromTitle(title) {
  const translitMap = {
    "а": "a", "б": "b", "в": "v", "г": "g", "д": "d", "е": "e", "ё": "yo",
    "ж": "zh", "з": "z", "и": "i", "й": "j", "к": "k", "л": "l", "м": "m",
    "н": "n", "о": "o", "п": "p", "р": "r", "с": "s", "т": "t", "у": "u",
    "ф": "f", "х": "kh", "ц": "ts", "ч": "ch", "ш": "sh", "щ": "shch",
    "ъ": "", "ы": "y", "ь": "", "э": "e", "ю": "yu", "я": "ya"
  };
  const transliterated = title
    .toLowerCase()
    .split("")
    .map((char) => translitMap[char] || char)
    .join("");
  return transliterated
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

function renderPreviewMarkdown(text) {
  if (!text) return "";
  let html = text
    .replace(/^### (.+)$/gm, "<h3 class=\"text-xl font-medium text-white mt-8 mb-3\">$1</h3>")
    .replace(/^## (.+)$/gm, "<h2 class=\"text-2xl font-medium text-white mt-10 mb-4\">$1</h2>")
    .replace(/^# (.+)$/gm, "<h1 class=\"text-3xl font-medium text-white mt-10 mb-4\">$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"font-medium text-neutral-100\">$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\d+\.\s(.+)$/gm, "<li class=\"text-lg font-extralight text-neutral-300 ml-6 list-decimal mb-2\">$1</li>")
    .replace(/^[-*]\s(.+)$/gm, "<li class=\"text-lg font-extralight text-neutral-300 ml-6 list-disc mb-2\">$1</li>")
    .replace(/\n\n/g, "</p><p class=\"text-lg font-extralight text-neutral-300 leading-relaxed mb-4\">")
    .replace(/\n/g, "<br/>");
  html = "<p class=\"text-lg font-extralight text-neutral-300 leading-relaxed mb-4\">" + html + "</p>";
  html = html.replace(/<p[^>]*><h/g, "<h").replace(/<\/h([1-3])><\/p>/g, "</h$1>");
  html = html.replace(/<p[^>]*><li/g, "<li").replace(/<\/li><\/p>/g, "</li>");
  html = html.replace(/<p[^>]*>\s*<\/p>/g, "");
  return html;
}

function stripTemplateFromContent(content) {
  const lines = content.split("\n");
  const filtered = lines.filter((line) => {
    const trimmed = line.trim();
    return !TEMPLATE_LINES.some((tl) => tl.trim() === trimmed && trimmed.length > 0);
  });
  return filtered.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export default function ArticleEditor({ article, onSave, onCancel, saving, expertId }) {
  const { user: currentUser } = useAuth();

  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    tags: [],
    meta_title: "",
    meta_description: "",
    status: "draft"
  });
  const [articleId, setArticleId] = useState(article?.id || null);
  const [articleSlug, setArticleSlug] = useState(article?.slug || null);
  const [tagInput, setTagInput] = useState("");
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [saved, setSaved] = useState(false);
  const [templateActive, setTemplateActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { scheduleAutoSave, cancelAutoSave, autoSaving, lastAutoSaved, resetFingerprint, draftIdRef, draftSlugRef } =
    useAutoSaveDraft(expertId);

  useEffect(() => {
    if (article) {
      setForm({
        title: article.title || "",
        excerpt: article.excerpt || "",
        content: article.content || "",
        tags: article.tags || [],
        meta_title: article.meta_title || "",
        meta_description: article.meta_description || "",
        status: article.status || "draft"
      });
      setArticleId(article.id || null);
      setArticleSlug(article.slug || null);
    }
  }, [article]);

  const handleFormChange = useCallback((newForm) => {
    setForm(newForm);
    if (newForm.status !== "published" && newForm.title?.trim()) {
      scheduleAutoSave({
        ...newForm,
        id: articleId,
        slug: articleSlug
      });
    }
  }, [scheduleAutoSave, articleId, articleSlug, draftIdRef]);

  const liveSlug = useMemo(() => {
    if (articleSlug) return articleSlug;
    return generateSlugFromTitle(form.title);
  }, [form.title, articleSlug]);

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag) && form.tags.length < 10) {
      const newForm = { ...form, tags: [...form.tags, tag] };
      handleFormChange(newForm);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleFormChange({ ...form, tags: form.tags.filter((t) => t !== tagToRemove) });
  };

  const handleSave = async (status) => {
    cancelAutoSave();
    const resolvedId = articleId || draftIdRef.current;
    const resolvedSlug = articleSlug || draftSlugRef.current;
    const result = await onSave({
      ...form,
      id: resolvedId,
      slug: resolvedSlug,
      status
    });
    if (result) {
      if (result.id) setArticleId(result.id);
      if (result.slug) setArticleSlug(result.slug);
      resetFingerprint();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleToggleTemplate = () => {
    if (!templateActive) {
      let newContent;
      if (form.content.trim().length === 0) {
        newContent = STRUCTURE_TEMPLATE;
      } else {
        newContent = form.content + "\n\n" + STRUCTURE_TEMPLATE;
      }
      handleFormChange({ ...form, content: newContent });
      setTemplateActive(true);
    } else {
      const cleaned = stripTemplateFromContent(form.content);
      handleFormChange({ ...form, content: cleaned });
      setTemplateActive(false);
    }
  };

  const wordCount = form.content.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));
  const metaTitleLen = (form.meta_title || form.title).length;
  const metaDescLen = (form.meta_description || form.excerpt).length;

  const autoSaveLabel = useMemo(() => {
    if (autoSaving) return "Сохранение...";
    if (lastAutoSaved) {
      const t = lastAutoSaved;
      return "Автосохранение " + t.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    }
    return null;
  }, [autoSaving, lastAutoSaved]);

  return (
    <div id="article-editor_container" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => { cancelAutoSave(); onCancel(); }}
          className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-lg font-extralight">Назад к статьям</span>
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          {autoSaveLabel && (
            <div className="flex items-center gap-1.5 text-neutral-500">
              {autoSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Wifi className="w-3.5 h-3.5 text-accent-500" />
              )}
              <span className="text-lg font-extralight">{autoSaveLabel}</span>
            </div>
          )}
          {saved && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-accent-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="text-lg font-extralight">Сохранено</span>
            </motion.div>
          )}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md border text-lg font-extralight transition-colors ${
              showPreview
                ? "border-accent-500 text-accent-400 bg-accent-500/10"
                : "border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white"
            }`}
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? "Редактор" : "Превью"}
          </button>
          <button
            onClick={() => handleSave("draft")}
            disabled={saving || !form.title.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white text-lg font-extralight transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Черновик
          </button>
          <button
            onClick={() => handleSave("published")}
            disabled={saving || !form.title.trim() || !form.content.trim()}
            className="flex items-center gap-2 px-5 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Опубликовать
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showPreview ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
          >
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-neutral-800">
              <Eye className="w-5 h-5 text-accent-400" />
              <span className="text-xl font-medium text-white">Превью статьи</span>
            </div>

            {form.title ? (
              <h1 className="text-3xl lg:text-4xl font-medium text-white mb-4 leading-tight">
                {form.title}
              </h1>
            ) : (
              <p className="text-xl font-extralight text-neutral-600 mb-4 italic">Заголовок не указан</p>
            )}

            {form.excerpt && (
              <p className="text-xl font-extralight text-neutral-400 mb-6 leading-relaxed">
                {form.excerpt}
              </p>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4 mb-8 pb-6 border-b border-neutral-800/50">
              <div className="flex items-center gap-3">
                {currentUser?.avatar_url ? (
                  <img
                    src={currentUser.avatar_url}
                    alt={currentUser.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {(currentUser?.name || "Э")[0]}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-lg font-medium text-neutral-200">
                    {currentUser?.name || "Эксперт"}
                  </p>
                  {currentUser?.title && (
                    <p className="text-lg font-extralight text-neutral-500">
                      {currentUser.title}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-lg font-extralight text-neutral-500">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{readingTime} мин чтения</span>
                </div>
                <span>{wordCount} слов</span>
              </div>
            </div>

            {form.tags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-6">
                {form.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight"
                  >
                    <Hash className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {form.content ? (
              <div
                className="prose-custom"
                dangerouslySetInnerHTML={{ __html: renderPreviewMarkdown(form.content) }}
              />
            ) : (
              <p className="text-lg font-extralight text-neutral-600 italic py-12 text-center">
                Содержимое статьи пока пусто
              </p>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="editor"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Заголовок статьи
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleFormChange({ ...form, title: e.target.value })}
                  placeholder="Как повысить конверсию лендинга на 300%"
                  className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-xl font-medium focus:outline-none focus:border-primary-500"
                />
                {form.title.trim() && (
                  <p className="mt-1.5 text-lg font-extralight text-neutral-600">
                    Ссылка: /articles/{liveSlug || "..."}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Краткое описание
                </label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => handleFormChange({ ...form, excerpt: e.target.value })}
                  rows={2}
                  placeholder="Краткое описание статьи для превью и поисковых систем (2-3 предложения)"
                  className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 resize-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-lg font-extralight text-neutral-400">
                    Текст статьи
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-extralight text-neutral-500">
                      {wordCount} слов / {readingTime} мин чтения
                    </span>
                    <button
                      onClick={handleToggleTemplate}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-lg font-extralight transition-colors ${
                        templateActive
                          ? "bg-primary-500/20 text-primary-300 ring-1 ring-primary-500/40"
                          : "bg-primary-500/10 text-primary-400 hover:bg-primary-500/20"
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      {templateActive ? "Убрать шаблон" : "Шаблон"}
                    </button>
                  </div>
                </div>
                <textarea
                  value={form.content}
                  onChange={(e) => handleFormChange({ ...form, content: e.target.value })}
                  rows={18}
                  placeholder="Напишите текст статьи. Используйте ## для подзаголовков, **жирный**, *курсив*, - для списков."
                  className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 resize-y font-mono leading-relaxed"
                />
              </div>

              <div>
                <label className="block text-lg font-extralight text-neutral-400 mb-2">
                  Теги
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {form.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight"
                    >
                      <Hash className="w-3.5 h-3.5" />
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-red-400 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }}
                    placeholder="Добавить тег..."
                    className="flex-1 px-4 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                  />
                  <button
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Добавить
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-6 rounded-md bg-neutral-900 border border-neutral-800">
        <button
          onClick={() => setShowSeoPanel(!showSeoPanel)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-accent-400" />
            <h3 className="text-xl font-medium text-white">SEO-оптимизация</h3>
          </div>
          <span className="text-lg font-extralight text-neutral-500">
            {showSeoPanel ? "Скрыть" : "Показать"}
          </span>
        </button>

        {showSeoPanel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-6 space-y-5"
          >
            <div className="p-4 rounded-md bg-accent-950/30 border border-accent-800/30">
              <p className="text-lg font-extralight text-accent-300 mb-3">Советы по SEO-оптимизации:</p>
              <ul className="space-y-1.5">
                {SEO_TIPS.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0 mt-0.5" />
                    <span className="text-lg font-extralight text-neutral-300">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-extralight text-neutral-400">
                  Мета-заголовок
                </label>
                <span className={`text-lg font-extralight ${metaTitleLen > 60 ? "text-yellow-400" : "text-neutral-500"}`}>
                  {metaTitleLen}/70
                </span>
              </div>
              <input
                type="text"
                value={form.meta_title}
                onChange={(e) => handleFormChange({ ...form, meta_title: e.target.value })}
                placeholder={form.title || "Будет использован заголовок статьи"}
                className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-extralight text-neutral-400">
                  Мета-описание
                </label>
                <span className={`text-lg font-extralight ${metaDescLen > 160 ? "text-red-400" : metaDescLen > 120 ? "text-accent-400" : "text-neutral-500"}`}>
                  {metaDescLen}/160
                </span>
              </div>
              <textarea
                value={form.meta_description}
                onChange={(e) => handleFormChange({ ...form, meta_description: e.target.value })}
                rows={3}
                placeholder={form.excerpt || "Будет использовано краткое описание статьи"}
                className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500 resize-none"
              />
            </div>

            <div className="p-4 rounded-md bg-neutral-800/50">
              <p className="text-lg font-extralight text-neutral-500 mb-2">Предварительный вид в Google / Яндекс:</p>
              <div className="space-y-1">
                <p className="text-lg font-medium text-blue-400 truncate">
                  {form.meta_title || form.title || "Заголовок статьи"}
                </p>
                <p className="text-lg font-extralight text-green-500 truncate">
                  obstuchim.ru/articles/{liveSlug || "url-statyi"}
                </p>
                <p className="text-lg font-extralight text-neutral-400 line-clamp-2">
                  {form.meta_description || form.excerpt || "Описание статьи для поисковых систем..."}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-md border text-lg font-extralight transition-colors ${
            showPreview
              ? "border-accent-500 text-accent-400 bg-accent-500/10"
              : "border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white"
          }`}
        >
          {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showPreview ? "Вернуться в редактор" : "Превью статьи"}
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSave("draft")}
            disabled={saving || !form.title.trim()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white text-lg font-extralight transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Черновик
          </button>
          <button
            onClick={() => handleSave("published")}
            disabled={saving || !form.title.trim() || !form.content.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Опубликовать
          </button>
        </div>
      </div>
    </div>
  );
}
